import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Shared audio player state (T2).
 *
 * Owns the single <audio> element for the whole app so playback survives tab
 * switches (the provider is mounted in App.js inside the authenticated
 * wrapper, above the tab content). All play/pause/seek actions and the
 * timeupdate/ended/loadedmetadata wiring live here — components must never
 * create their own Audio instances.
 *
 * Episode object shape expected by play():
 *   { id, title, audioUrl, scheduleName?, sources?, duration? (display string),
 *     durationSeconds?, pubDate?, episodeFormat? }
 */

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const noticeTimerRef = useRef(null);

  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Transient player-originated status text (episode finished, playback
  // error). Consumers may surface it in their own message area.
  const [notice, setNotice] = useState('');

  const showNotice = useCallback((text) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice(text);
    noticeTimerRef.current = setTimeout(() => setNotice(''), 3000);
  }, []);

  // Lazily create the single Audio instance and wire its events once.
  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.preload = 'metadata';

      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });

      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
        showNotice('✅ Podcast finished playing');
      });

      audio.addEventListener('error', () => {
        // Ignore the spurious error fired when src is cleared on teardown.
        if (!audio.src || audio.src === window.location.href) return;
        console.error('❌ Audio playback error:', {
          error: audio.error,
          networkState: audio.networkState,
          readyState: audio.readyState,
          src: audio.src,
        });
        setIsPlaying(false);
        showNotice(`❌ Error playing audio: ${audio.error?.message || 'Unknown error'}`);
      });

      audioRef.current = audio;
    }
    return audioRef.current;
  }, [showNotice]);

  // Stop audio when the provider unmounts (e.g. sign-out).
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, []);

  /**
   * Start an episode. Tapping the episode that is already loaded toggles
   * pause/resume instead of restarting it. Resolves to 'playing' | 'paused'.
   */
  const play = useCallback(
    async (episode) => {
      if (!episode?.audioUrl) {
        throw new Error('No audio file available. All podcasts must be generated with AWS Polly.');
      }
      const audio = getAudio();
      const isSame = currentlyPlaying?.id === episode.id;

      if (isSame && isPlaying) {
        audio.pause();
        setIsPlaying(false);
        return 'paused';
      }

      if (!isSame) {
        audio.pause();
        audio.src = episode.audioUrl;
        audio.currentTime = 0;
        setCurrentTime(0);
        setDuration(0);
        setCurrentlyPlaying(episode);
      }

      try {
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        setIsPlaying(false);
        throw err;
      }
      return 'playing';
    },
    [currentlyPlaying, isPlaying, getAudio]
  );

  /** Toggle pause/resume of the currently loaded episode. */
  const togglePause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentlyPlaying) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error('❌ Error resuming playback:', err);
          showNotice('❌ Error playing audio. Please check your browser settings.');
        });
    }
  }, [currentlyPlaying, isPlaying, showNotice]);

  /** Seek to a fraction (0..1) of the loaded episode's duration. */
  const seek = useCallback(
    (fraction) => {
      const audio = audioRef.current;
      if (!audio || !duration || !isFinite(duration)) return;
      const clamped = Math.min(1, Math.max(0, fraction));
      const newTime = clamped * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration]
  );

  const value = useMemo(
    () => ({
      currentlyPlaying,
      isPlaying,
      currentTime,
      duration,
      notice,
      play,
      togglePause,
      seek,
    }),
    [currentlyPlaying, isPlaying, currentTime, duration, notice, play, togglePause, seek]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return ctx;
}
